// src/pages/admin/SecretKillSwitchLab.jsx
import React, { useEffect, useState } from "react";
import { supabase } from "../../utils/supabase";

const FLAG_KEY = "global_lock_lab";

export default function SecretKillSwitchLab() {
  const params = new URLSearchParams(window.location.search);
  const urlKey = params.get("key") || "";
  const adminKey = import.meta.env.VITE_ADMIN_KEY || "";

  const [authz, setAuthz] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [userId, setUserId] = useState(null);

  // 1) Validar llave URL vs env
  useEffect(() => {
    setAuthz(!!adminKey && !!urlKey && adminKey === urlKey);
  }, [adminKey, urlKey]);

  // 2) Traer sesión (si no hay, no dejamos guardar)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        setUserId(data?.user?.id ?? null);
      } catch {
        setUserId(null);
      }
    })();
  }, []);

  async function fetchFlag() {
    setError("");
    setInfo("");
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from("app_flags")
        .select("enabled, message")
        .eq("key", FLAG_KEY)
        .single();

      if (error) throw error;

      setEnabled(!!data?.enabled);
      setMessage(data?.message || "");
    } catch (e) {
      console.error(e);
      setError(e?.message || "Error al cargar el flag");
    } finally {
      setLoading(false);
    }
  }

  // 3) Cargar estado actual del flag
  useEffect(() => {
    if (!authz) {
      setLoading(false);
      return;
    }
    fetchFlag();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authz]);

  async function save() {
    setSaving(true);
    setError("");
    setInfo("");

    try {
      // Requerir login sí o sí para guardar
      const { data: userData, error: userErr } = await supabase.auth.getUser();
      if (userErr) throw userErr;

      const uid = userData?.user?.id ?? null;
      setUserId(uid);

      if (!uid) {
        throw new Error("Debes iniciar sesión para poder bloquear/habilitar la aplicación.");
      }

      // UPDATE + SELECT para confirmar que realmente cambió (y no quedó en 0 filas)
      const { data, error } = await supabase
        .from("app_flags")
        .update({
          enabled,
          message: message || null,
          updated_by: uid,
          updated_at: new Date().toISOString(),
        })
        .eq("key", FLAG_KEY)
        .select("key, enabled, message")
        .single();

      if (error) throw error;

      // Si llegamos acá, cambió de verdad
      setInfo(`Guardado OK. Estado: ${data?.enabled ? "BLOQUEADA" : "HABILITADA"}`);
    } catch (e) {
      console.error(e);
      setError(e?.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  }

  // Sin llave correcta -> no deja pasar
  if (!authz) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0f1115] text-gray-100 p-6">
        <div className="max-w-lg w-full rounded-xl border border-white/10 p-5 bg-black/30">
          <div className="text-lg font-semibold">Acceso restringido</div>
          <p className="mt-2 text-sm opacity-80">
            Proporciona la llave correcta en la URL:{" "}
            <code>?key=TU_LLAVE_SECRETA</code>
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0f1115] text-gray-100">
        Cargando...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1115] text-gray-100 p-6">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="rounded-2xl border border-white/10 p-5 bg-[#12161d]">
          <div className="text-xl font-semibold">Kill-switch - Laboratorio</div>
          <p className="text-sm opacity-80 mt-1">
            Activa o desactiva el bloqueo global de esta aplicación de laboratorio.
          </p>

          {/* Estado de login */}
          <div className="mt-3 text-xs opacity-80">
            Usuario:{" "}
            {userId ? (
              <span className="text-emerald-300">logueado</span>
            ) : (
              <span className="text-rose-300">NO logueado (no podrás guardar)</span>
            )}
          </div>

          <div className="mt-5 flex items-center gap-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <span>Bloquear aplicación</span>
            </label>
            <button
              onClick={fetchFlag}
              className="px-3 py-1.5 rounded-lg border border-white/15 hover:bg-white/10 transition text-sm"
            >
              Refrescar
            </button>
          </div>

          <div className="mt-4">
            <label className="text-sm opacity-90">Mensaje a mostrar</label>
            <textarea
              className="mt-1 w-full min-h-[120px] rounded-lg border border-white/10 bg-black/30 p-3"
              placeholder="Motivo del bloqueo, por ejemplo: mantenimiento, etc."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          {error && <div className="mt-3 text-rose-300 text-sm">Error: {error}</div>}
          {info && <div className="mt-3 text-emerald-300 text-sm">{info}</div>}

          <div className="mt-5 flex items-center gap-2">
            <button
              disabled={saving || !userId}
              onClick={save}
              className={`px-4 py-2 rounded-lg transition ${
                saving || !userId
                  ? "bg-emerald-600/40 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              {saving ? "Guardando..." : enabled ? "Guardar (bloquear)" : "Guardar (habilitar)"}
            </button>

            <a
              className="px-3 py-2 rounded-lg border border-white/15 hover:bg-white/10 transition"
              href="/"
            >
              Ir al inicio
            </a>

            {!userId && (
              <a
                className="px-3 py-2 rounded-lg border border-white/15 hover:bg-white/10 transition"
                href="/login"
              >
                Ir a login
              </a>
            )}
          </div>
        </div>

        <div className="text-xs opacity-70">
          Usar env: <code>VITE_ADMIN_KEY</code> y entrar a{" "}
          <code>/_admin/lock?key=TU_LLAVE_SECRETA</code>.
        </div>
      </div>
    </div>
  );
}
